import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  type HydratedTable,
  useBulkRestoreAssets,
  useBulkTrashAssets,
  useSplitTreatments,
  useTableConnectedAssets,
} from "@zapier/client";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  SkeletonLoader,
  SkeletonText,
  Spacer,
  Text,
} from "@zapier/design-system";
import {
  AssetTypes,
  TABLE_ASSET_ID_PREFIX,
  useNotification,
} from "@zapier/utils";

import { useDeleteTable } from "./useDeleteTable";
import { useEmitTableDeleteModalEvent } from "@zapier/shared/src/events";
import {
  LinkedAssetsSection,
  LinkedAssetsList,
  TrashNotification,
} from "@zapier/shared";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  table: HydratedTable | undefined;
  folderId?: string;
}

export function TableDeleteModal({ isOpen, onClose, table, folderId }: Props) {
  const t = useTranslations("table-delete-modal");
  const { createNotification } = useNotification();

  const { deleteTable, isPending } = useDeleteTable(table, folderId);
  const bulkTrashAssets = useBulkTrashAssets({ folderId });
  const bulkRestoreAssets = useBulkRestoreAssets({ folderId });
  const { isZapAndCanvasUnifiedTrashEnabled, isTableUnifiedTrashEnabled } =
    useSplitTreatments();
  const { emit } = useEmitTableDeleteModalEvent();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
    }
  };

  const {
    transformedAssets,
    hasBlockingConnections,
    hasNonBlockingConnections,
    hasViews,
    isFetching: isFetchingAssets,
  } = useTableConnectedAssets(isOpen ? table?.id : undefined);

  const isView = table?.kind === "virtual_table";
  // Table views stay hard-delete-only; trash never applies to them.
  const isSoftDeletedEnabledForTable =
    !isView && isZapAndCanvasUnifiedTrashEnabled && isTableUnifiedTrashEnabled;

  const tableName = table?.name ?? "";
  const viewCount = useMemo(
    () =>
      transformedAssets.filter((asset) => asset.type === AssetTypes.TableView)
        .length,
    [transformedAssets],
  );
  // Hard-delete path (flags off): lists every connection, including views,
  // by name, with no Alert wrapper (LinkedAssetsSection isn't used here).
  const showLegacyLinkedAssetsList =
    !isSoftDeletedEnabledForTable &&
    (hasBlockingConnections || hasNonBlockingConnections || hasViews);

  const handleUndoTrash = async (assetIds: string[]) => {
    try {
      const { failedAssetIds } = await bulkRestoreAssets.mutateAsync({
        assetIds,
      });

      if (failedAssetIds.length > 0) {
        createNotification({
          message: t("notifications.restore-error", { tableName }),
          type: "error",
        });
        return;
      }

      createNotification({
        message: t("notifications.restore-success", { tableName }),
        type: "success",
      });
    } catch {
      createNotification({
        message: t("notifications.restore-error", { tableName }),
        type: "error",
      });
    }
  };

  const handleMoveToTrash = async () => {
    emit("confirm");
    try {
      const trashedIds = [`${TABLE_ASSET_ID_PREFIX}${table?.id}`];
      const { failedAssetIds } = await bulkTrashAssets.mutateAsync({
        assetIds: trashedIds,
      });

      if (failedAssetIds.length > 0) {
        createNotification({
          message: t("notifications.trash-error", { tableName }),
          type: "error",
        });
        return;
      }

      createNotification({
        title: t("notifications.trash-success", { tableName }),
        message: (
          <TrashNotification onUndo={() => handleUndoTrash(trashedIds)} />
        ),
        type: "success",
      });
    } catch {
      createNotification({
        message: t("notifications.trash-error", { tableName }),
        type: "error",
      });
    } finally {
      onClose();
    }
  };

  const modalTitle = isSoftDeletedEnabledForTable
    ? t("headers.trash-table", { tableName })
    : hasBlockingConnections
      ? t("headers.unable-to-delete-table", { tableName })
      : t("headers.delete-table", { tableName, isView: String(isView) });

  const modalText = isSoftDeletedEnabledForTable
    ? t("messages.trash-explanation", {
        hasViews: String(hasViews),
        viewCount: hasViews ? viewCount : 0,
      })
    : hasBlockingConnections
      ? t("messages.blocking-connections-warning")
      : hasNonBlockingConnections
        ? t("messages.non-blocking-connections-warning")
        : hasViews
          ? t("messages.permanent-deletion-with-views-warning")
          : t("messages.permanent-deletion-warning", {
              isView: String(isView),
            });

  const isMutating = isSoftDeletedEnabledForTable
    ? bulkTrashAssets.isPending
    : isPending;

  if (!table) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>{modalTitle}</ModalDialogHeader>

        <ModalDialogBody>
          {isFetchingAssets && (
            <SkeletonLoader>
              <SkeletonText lines={2} width="100%" />
            </SkeletonLoader>
          )}
          {!isFetchingAssets && (
            <>
              <Text color="TextWeaker">{modalText}</Text>
              {isSoftDeletedEnabledForTable ? (
                <LinkedAssetsSection
                  hasBlockingConnections={hasBlockingConnections}
                  hasNonBlockingConnections={hasNonBlockingConnections}
                  transformedAssets={transformedAssets}
                />
              ) : (
                showLegacyLinkedAssetsList && (
                  <>
                    <Spacer height={12} />
                    <LinkedAssetsList linkedAssets={transformedAssets} />
                  </>
                )
              )}
            </>
          )}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isMutating}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          {isSoftDeletedEnabledForTable ? (
            <Button
              isDisabled={isMutating}
              isPending={isMutating || isFetchingAssets}
              onPress={handleMoveToTrash}
              variant="danger"
            >
              {t("buttons.move-to-trash")}
            </Button>
          ) : (
            <Button
              isDisabled={isMutating || hasBlockingConnections}
              isPending={isMutating || isFetchingAssets}
              onPress={() => {
                emit("confirm");
                deleteTable();
                onClose();
              }}
              variant="danger"
            >
              {t("buttons.delete")}
            </Button>
          )}
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}

// For ease of use during `dynamic` import
export default TableDeleteModal;
